export const uiBasicsTranslations = {
  en: {
    hero: {
      badge: 'Standalone Course',
      title: 'UI/UX Basics',
      subtitle: 'Create beautiful, intuitive interfaces that users love',
      note: 'This is a separate course, not included in the Full Program'
    },
    content: {
      badge: '🎨 Complete Design Path',
      description: 'Master the fundamentals of user interface design. Learn to create visually appealing and user-friendly interfaces without being a professional designer.'
    },
    features: {
      title: 'What\'s Included',
      items: [
        {
          title: 'Design Thinking',
          description: 'Learn to think like a designer and guide AI to build your vision'
        },
        {
          title: 'Visual Design',
          description: 'Master colors, typography, and visual hierarchy'
        },
        {
          title: 'Figma Mastery',
          description: 'From basics to professional workflows'
        },
        {
          title: 'Design Systems',
          description: 'Build reusable, consistent component libraries'
        },
        {
          title: 'UI Patterns',
          description: 'Learn patterns that work on mobile and web'
        },
        {
          title: 'Design to AI',
          description: 'Communicate your designs clearly so AI can implement them'
        }
      ]
    },
    curriculum: {
      title: 'Program Curriculum',
      description: 'A complete journey from design thinking to communicating your vision to AI for implementation.'
    },
    tools: {
      title: 'Tools You\'ll Use',
      items: [
        {
          name: 'Figma',
          description: 'Industry-standard design tool'
        },
        {
          name: 'Tailwind CSS',
          description: 'Utility-first CSS framework'
        },
        {
          name: 'AI Design Tools',
          description: 'Generate designs with AI assistance'
        }
      ]
    },
    cta: {
      goToDashboard: 'Go to Dashboard',
      backToHome: 'Back to Home',
      startDesigning: 'Start Designing',
      viewExamples: 'View Examples'
    },
    moduleCards: {
      module1: {
        badge: 'Module 1',
        title: 'Design Thinking for Product Builders',
        details: `✓ What is UI/UX and why it matters
✓ User-centered design basics
✓ The difference between UI and UX
✓ How to think about design as a product builder
✓ Common design mistakes beginners make
✓ Building empathy for users

"Good design is invisible — users don't notice it, they just feel it works"

Module Description

This module introduces you to design thinking as a product builder.

You don't need to become a designer — but you need to understand how designers think so you can guide AI to build your vision.

You will learn:
• what UI and UX actually mean
• why user-centered design matters for product success
• how to avoid common mistakes when designing products
• how to build empathy for your users

Design thinking is a skill that makes your products better — and helps you communicate clearly with AI when building interfaces.

This module gives you the mental framework to think about design intentionally.`
      },
      module2: {
        badge: 'Module 2',
        title: 'Visual Design Fundamentals',
        details: `✓ Color theory & color psychology
✓ How to choose and combine colors
✓ Typography basics & font selection
✓ Visual hierarchy — guiding the eye
✓ Spacing, alignment & visual balance
✓ Contrast & accessibility basics

"Visual design is a language — learn the vocabulary"

Module Description

In this module, you learn the building blocks of visual design.

These are the fundamentals that make interfaces look professional:
• color theory and how colors affect emotions
• typography and how fonts communicate personality
• spacing and alignment that create visual harmony
• contrast that ensures accessibility

You will understand why some interfaces feel "right" and others feel "off."

This module gives you the vocabulary to talk about design and the skills to create visually balanced interfaces.

No artistic talent required — just understanding of principles.`
      },
      module3: {
        badge: 'Module 3',
        title: 'Figma Essentials',
        details: `✓ Figma interface overview
✓ Creating frames & artboards
✓ Working with shapes, text & images
✓ Components & instances
✓ Auto-layout for responsive design
✓ Prototyping basics & interactions
✓ Exporting assets for AI implementation

"Figma is where ideas become visual reality"

Module Description

Figma is the industry standard for UI design — and this module teaches you everything you need.

You will learn:
• how to navigate Figma's interface
• how to create and organize design files
• how to build reusable components
• how to use auto-layout for flexible designs
• how to create interactive prototypes
• how to prepare designs for AI implementation

By the end of this module, you'll be able to create professional mockups and prototypes that you can describe to AI for building.

Figma is a tool every modern product builder should know.`
      },
      module4: {
        badge: 'Module 4',
        title: 'Design Systems & Components',
        details: `✓ What is a design system
✓ Design tokens: colors, fonts, spacing
✓ Building a component library
✓ Consistency across your product
✓ Naming conventions & organization
✓ Documenting your design system

"A design system is a product's visual vocabulary"

Module Description

A design system is what separates amateur products from professional ones.

In this module, you learn how to build and maintain a design system:
• what design tokens are and why they matter
• how to create a consistent color palette
• how to build reusable components
• how to organize and name things properly
• how to document your system for future use

You will understand how companies like Airbnb, Google, and Apple maintain consistency across their products.

A design system saves time and ensures quality — this module shows you how to build one.`
      },
      module5: {
        badge: 'Module 5',
        title: 'Mobile & Web Design Patterns',
        details: `✓ Navigation patterns (tabs, drawers, headers)
✓ Form design best practices
✓ Cards, lists, and grid layouts
✓ Mobile-first vs responsive design
✓ iOS vs Android design guidelines
✓ Web-specific patterns
✓ Common UI patterns that work

"Patterns exist because they work — learn them before breaking them"

Module Description

Design patterns are solutions that have been proven to work.

In this module, you learn the most important UI patterns:
• navigation: tabs, drawers, bottom bars, headers
• content: cards, lists, grids, feeds
• forms: inputs, validation, error states
• mobile: iOS and Android specific patterns
• web: headers, footers, sidebars, modals

You will understand:
• which patterns work best for which situations
• how mobile and web patterns differ
• when to follow guidelines and when to innovate

Don't reinvent the wheel — learn the patterns that users already understand.`
      },
      module6: {
        badge: 'Module 6',
        title: 'From Design to AI Implementation',
        details: `✓ How to describe designs to AI clearly
✓ Understanding CSS basics for better communication
✓ Tailwind CSS vocabulary for AI prompts
✓ AI tools for design-to-code workflow
✓ Reviewing and refining AI-generated interfaces
✓ Ensuring design consistency in AI output
✓ Iterating with AI until it matches your vision

"The key to great AI implementation is clear design communication"

Module Description

The final step: turning your designs into real, working interfaces with AI.

In this module, you learn how to communicate your designs to AI effectively:
• how to describe Figma designs in AI-friendly language
• what CSS and Tailwind terms help AI understand your vision
• how to use AI tools to generate interfaces from your designs
• how to review AI output and request improvements
• how to maintain design consistency across AI-generated code

You will also learn the feedback loop — how to iterate with AI until the result matches your vision perfectly.

This module completes the circle: from thinking about design, to creating designs in Figma, to guiding AI to implement them.`
      }
    }
  },
  uk: {
    hero: {
      badge: 'Окремий курс',
      title: 'Основи UI/UX',
      subtitle: 'Створюйте красиві, інтуїтивні інтерфейси, які подобаються користувачам',
      note: 'Це окремий курс, не входить до Full Program'
    },
    content: {
      badge: '🎨 Повний шлях дизайну',
      description: 'Опануйте основи дизайну користувацького інтерфейсу. Навчіться створювати візуально привабливі та зручні інтерфейси без професійного дизайнерського досвіду.'
    },
    features: {
      title: 'Що включено',
      items: [
        {
          title: 'Дизайн-мислення',
          description: 'Навчіться думати як дизайнер і направляти AI для реалізації вашого бачення'
        },
        {
          title: 'Візуальний дизайн',
          description: 'Опануйте кольори, типографіку та візуальну ієрархію'
        },
        {
          title: 'Майстерність Figma',
          description: 'Від основ до професійних робочих процесів'
        },
        {
          title: 'Дизайн-системи',
          description: 'Створюйте багаторазові, узгоджені бібліотеки компонентів'
        },
        {
          title: 'UI патерни',
          description: 'Вивчіть патерни, що працюють на мобільних та вебі'
        },
        {
          title: 'Від дизайну до AI',
          description: 'Комунікуйте свої дизайни чітко, щоб AI міг їх реалізувати'
        }
      ]
    },
    curriculum: {
      title: 'Навчальна програма',
      description: 'Повний шлях від дизайн-мислення до комунікації вашого бачення AI для реалізації.'
    },
    tools: {
      title: 'Інструменти, які ви використовуватимете',
      items: [
        {
          name: 'Figma',
          description: 'Індустріальний стандарт дизайн-інструменту'
        },
        {
          name: 'Tailwind CSS',
          description: 'CSS фреймворк на основі утиліт'
        },
        {
          name: 'AI Design Tools',
          description: 'Генеруйте дизайни з допомогою AI'
        }
      ]
    },
    cta: {
      goToDashboard: 'Перейти до панелі',
      backToHome: 'На головну',
      startDesigning: 'Почати дизайн',
      viewExamples: 'Переглянути приклади'
    },
    moduleCards: {
      module1: {
        badge: 'Модуль 1',
        title: 'Дизайн-мислення для творців продуктів',
        details: `✓ Що таке UI/UX і чому це важливо
✓ Основи дизайну, орієнтованого на користувача
✓ Різниця між UI та UX
✓ Як думати про дизайн як творець продуктів
✓ Типові помилки початківців у дизайні
✓ Розвиток емпатії до користувачів

"Хороший дизайн невидимий — користувачі його не помічають, вони просто відчувають, що все працює"

Опис модуля

Цей модуль знайомить вас з дизайн-мисленням як творця продуктів.

Вам не потрібно ставати дизайнером — але потрібно розуміти, як думають дизайнери, щоб направляти AI для реалізації вашого бачення.

Ви дізнаєтесь:
• що насправді означають UI та UX
• чому дизайн, орієнтований на користувача, важливий для успіху продукту
• як уникнути типових помилок при проектуванні продуктів
• як розвинути емпатію до користувачів

Дизайн-мислення — це навичка, яка робить ваші продукти кращими — і допомагає чітко комунікувати з AI при створенні інтерфейсів.

Цей модуль дає вам ментальну основу для усвідомленого ставлення до дизайну.`
      },
      module2: {
        badge: 'Модуль 2',
        title: 'Основи візуального дизайну',
        details: `✓ Теорія кольору та психологія кольору
✓ Як обирати та поєднувати кольори
✓ Основи типографіки та вибір шрифтів
✓ Візуальна ієрархія — керування поглядом
✓ Відступи, вирівнювання та візуальний баланс
✓ Контраст та основи доступності

"Візуальний дизайн — це мова — вивчіть словниковий запас"

Опис модуля

У цьому модулі ви вивчаєте будівельні блоки візуального дизайну.

Це основи, які роблять інтерфейси професійними:
• теорія кольору та як кольори впливають на емоції
• типографіка та як шрифти передають індивідуальність
• відступи та вирівнювання, що створюють візуальну гармонію
• контраст, що забезпечує доступність

Ви зрозумієте, чому деякі інтерфейси відчуваються "правильними", а інші — "не так".

Цей модуль дає вам словниковий запас для розмови про дизайн та навички створення візуально збалансованих інтерфейсів.

Художній талант не потрібен — лише розуміння принципів.`
      },
      module3: {
        badge: 'Модуль 3',
        title: 'Основи Figma',
        details: `✓ Огляд інтерфейсу Figma
✓ Створення фреймів та артбордів
✓ Робота з формами, текстом та зображеннями
✓ Компоненти та екземпляри
✓ Auto-layout для адаптивного дизайну
✓ Основи прототипування та інтерактивності
✓ Підготовка ресурсів для AI реалізації

"Figma — це місце, де ідеї стають візуальною реальністю"

Опис модуля

Figma — це індустріальний стандарт для UI дизайну — і цей модуль навчить вас всьому необхідному.

Ви дізнаєтесь:
• як орієнтуватися в інтерфейсі Figma
• як створювати та організовувати дизайн-файли
• як будувати багаторазові компоненти
• як використовувати auto-layout для гнучких дизайнів
• як створювати інтерактивні прототипи
• як підготувати дизайни для AI реалізації

Після цього модуля ви зможете створювати професійні макети та прототипи, які зможете описати AI для побудови.

Figma — інструмент, який повинен знати кожен сучасний творець продуктів.`
      },
      module4: {
        badge: 'Модуль 4',
        title: 'Дизайн-системи та компоненти',
        details: `✓ Що таке дизайн-система
✓ Дизайн-токени: кольори, шрифти, відступи
✓ Створення бібліотеки компонентів
✓ Узгодженість у продукті
✓ Правила найменування та організація
✓ Документування дизайн-системи

"Дизайн-система — це візуальний словник продукту"

Опис модуля

Дизайн-система — це те, що відрізняє аматорські продукти від професійних.

У цьому модулі ви навчитесь створювати та підтримувати дизайн-систему:
• що таке дизайн-токени і чому вони важливі
• як створити узгоджену кольорову палітру
• як будувати багаторазові компоненти
• як правильно організовувати та називати елементи
• як документувати систему для майбутнього використання

Ви зрозумієте, як компанії на кшталт Airbnb, Google та Apple підтримують узгодженість у своїх продуктах.

Дизайн-система економить час та забезпечує якість — цей модуль покаже вам, як її побудувати.`
      },
      module5: {
        badge: 'Модуль 5',
        title: 'Патерни мобільного та веб-дизайну',
        details: `✓ Патерни навігації (таби, меню, хедери)
✓ Найкращі практики дизайну форм
✓ Картки, списки та сітки
✓ Mobile-first vs адаптивний дизайн
✓ Настанови iOS vs Android
✓ Веб-специфічні патерни
✓ Поширені UI патерни, що працюють

"Патерни існують, бо вони працюють — вивчіть їх перед тим, як порушувати"

Опис модуля

Патерни дизайну — це рішення, які довели свою ефективність.

У цьому модулі ви вивчите найважливіші UI патерни:
• навігація: таби, бокові меню, нижні панелі, хедери
• контент: картки, списки, сітки, стрічки
• форми: поля вводу, валідація, стани помилок
• мобільний: специфічні патерни iOS та Android
• веб: хедери, футери, сайдбари, модальні вікна

Ви зрозумієте:
• які патерни найкраще підходять для яких ситуацій
• як відрізняються мобільні та веб-патерни
• коли слідувати настановам, а коли впроваджувати нове

Не винаходьте велосипед — вивчіть патерни, які користувачі вже розуміють.`
      },
      module6: {
        badge: 'Модуль 6',
        title: 'Від дизайну до AI реалізації',
        details: `✓ Як чітко описувати дизайни для AI
✓ Розуміння основ CSS для кращої комунікації
✓ Словник Tailwind CSS для AI промптів
✓ AI інструменти для робочого процесу дизайн-в-код
✓ Перевірка та вдосконалення AI-згенерованих інтерфейсів
✓ Забезпечення узгодженості дизайну в результатах AI
✓ Ітерації з AI до відповідності вашому баченню

"Ключ до чудової AI реалізації — це чітка комунікація дизайну"

Опис модуля

Фінальний крок: перетворення ваших дизайнів на реальні, працюючі інтерфейси за допомогою AI.

У цьому модулі ви навчитесь ефективно комунікувати свої дизайни AI:
• як описувати дизайни Figma мовою, зрозумілою AI
• які терміни CSS та Tailwind допомагають AI зрозуміти ваше бачення
• як використовувати AI інструменти для генерації інтерфейсів з ваших дизайнів
• як перевіряти результати AI та запитувати покращення
• як підтримувати узгодженість дизайну в AI-згенерованому коді

Ви також вивчите цикл зворотного зв'язку — як працювати ітераційно з AI, поки результат ідеально не відповідатиме вашому баченню.

Цей модуль завершує коло: від мислення про дизайн, до створення дизайнів у Figma, до направлення AI для їх реалізації.`
      }
    }
  }
};
